<template>
  <b-table 
    :class="['transactions-dates-table gk-table gk-table--responsive']"
    :items="transactions" 
    :fields="transactionGroupFields"
    thead-class="d-none"
    :busy="loading"
    show-empty
    table-busy
  >
    <template #cell(date)="data">
      <span class="transaction-date">{{ data.value | formatDate }}</span>
    </template>

    <template #row-details="row">
      <b-table 
        :class="['transactions-table gk-table gk-table--responsive']"
        @row-clicked="rowClicked"
        :items="row.item.transactions" 
        :fields="transactionFields"
        thead-class="d-none"
        primary-key="id"
        :tbody-tr-class="rowClass" 
      >
        <template #cell(icon)="data">
          <div class="transaction-td-wrap">
            <font-awesome-icon :icon="getTransactionIcon(data.item.status)" />
          </div>
        </template>
        <template #cell(status)="data">
          <div class="transaction-td-wrap">
            {{ getTransactionStatus(data.value) }}
          </div>
        </template>
        <template #cell(descamount)="data">
          <div class="transaction-td-wrap">
            <div class="transaction-desc-wrap">
              <div v-b-tooltip="{ trigger: 'hover', delay: { show: 500 } }" :title="data.item.description" class="transaction-description">
                {{ data.item.description }}
              </div>
              <div v-if="data.item.name" v-b-tooltip="{ trigger: 'hover', delay: { show: 500 } }" :title="data.item.name" class="transaction-name">
                {{ data.item.name }}
              </div>
              <div v-if="data.item.target_account" v-b-tooltip="{ trigger: 'hover', delay: { show: 500 } }" :title="data.item.target_account" class="transaction-target_account">
                {{ data.item.target_account }}
              </div>
            </div>
            <div class="transaction-amount">€ {{ data.item.amount | formatNumber }}</div>
          </div>
        </template>
      </b-table>
    </template>

    <template #empty="">
      <EmptyState>
        <template v-slot:image>
          <img src="/static/img/empty-states/empty-state-cost.png">
        </template>
        <template v-slot:title>
          {{ $gettext('We couldn\'t find any transactions') }}
        </template>
        <template v-slot:content>
          {{ $gettext('Try refreshing this transaction list or choosing a different account or filters.') }}
        </template>
        <template v-slot:cta>
          <GekkoButton 
            @click="$emit('refresh-transactions', false)"
            :text="$gettext('Refresh transactions')"
            icon="fa-solid fa-arrows-rotate"
            :disabled="refreshButtonEnabled"
          />
        </template>
      </EmptyState>
    </template>
    <template #table-busy="">
      <LoadingSpinner min-height />
    </template>
    <template slot="bottom-row" v-if="nextListUrl">
      <td :colspan="transactionGroupFields.length" class="table-load-more">
        <GekkoButton 
          v-if="nextListUrl && !fetchingData" 
          @click="handleScroll()"
          id="load_more_button" 
          class="text-info"
          variant="link" 
          :text="$gettext('Load more transactions')" 
        />
        <LoadingSpinner v-if="fetchingData" />
      </td>
    </template>

  </b-table>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex"
import EmptyState from "@/components/ui/EmptyState"
import GekkoButton from "@/components/GekkoButton"
import BankTransactionService from '@/store/services/bank.transaction.service.js'
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"

export default {
  name: "BankTransactionsTable",
  components: {
    EmptyState,
    GekkoButton,
    LoadingSpinner
  },
  data () {
    return {
      transactions: null,
      nextListUrl: null,
      loading: false,
      fetchingData: false,
      filters: { 
        extended: 'True',
        company_profile: null
      },
      transactionGroupFields: [
        { key: 'date', tdClass: 'col-dategroup' }
      ],
      transactionFields: [
        { key: 'status', tdClass: 'col-transaction-status' },
        { key: 'descamount', tdClass: 'col-descamount' }
      ],
    }
  },
  computed: {
    ...mapGetters ({ 
      selectedAccount: "bankStore/getSelectedAccount",
      getCompanyId: 'main/getCompanyId'
    }),
    refreshButtonEnabled(){
      if(!this.selectedAccount){
        return false
      }
      return this.selectedAccount.provider !== "" || this.selectedAccount.provider !== null;
    }
  },
  watch: {
    selectedAccount() {
      this.getTransactions();
    }
  },
  created () {
    this.filters.company_profile = this.getCompanyId

    window.addEventListener('scroll', this.handleScroll)
    this.getTransactions();

  },
  destroyed () {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    ...mapMutations({ 
      setSelectedAccountBalance: 'bankStore/SET_SELECTED_ACCOUNT_BALANCE'
    }),
    ...mapActions ({
      loadBankAccounts: "bankStore/loadBankAccounts",
    }),
    rowClicked (item) {
      this.$emit('row-clicked', item);
    },
    rowClass (item) {
      if(item && item.type) return 'transaction-row-' + item.type + ' ' + 'transaction-row-' + item.status
    },
    getTransactionIcon (status) {
      return (status === 'unassigned') ? 'fa-solid fa-question' : 'fa-solid fa-check'
    },
    getTransactionStatus (status) {
      let text = ''
      switch (status) {
        case 'unassigned':
          text = this.$gettext('Not assigned')
          break;
        case 'matched_with_cost':
          text = this.$gettext('Cost')
          break;
        case 'matched_with_invoice':
          text = this.$gettext('Income')
          break;
        case 'private':
          text = this.$gettext('Private')
          break;
        case 'taxes':
          text = this.$gettext('Taxes')
          break;
        case 'others':
          text = this.$gettext('Others')
          break;
        case 'batch_payments':
          text = this.$gettext('Batch Payments')
          break;
        case 'loan':
          text = this.$gettext('Loan')
          break;
        case 'savings':
          text = this.$gettext('Savings')
          break;
      }
      return text
    },
    getTransactions () {
      let filters = {}
      if (this.selectedAccount) {
        filters = { ...this.$store.getters["filterStore/getFilter"], ...this.filters, account: this.selectedAccount.id }
        filters.refresh = true;
        this.loading = true;
        // this.selectedAccount.company_profile == this.getCompanyId this is done to avoid a concurrency issue when switching companies
        if (!this.fetchingData && (this.selectedAccount.company_profile == this.getCompanyId)) {
          this.fetchingData = true;
          return BankTransactionService.list(filters).then((response) => {
            this.nextListUrl = response.data.next
            this.fetchingData = false
            this.loading = false
            this.transactions = this.groupTransactionsByDate(response.data.results)

            // Account amount refresh (temporary fix)
            this.loadBankAccounts().then(() => {
              this.setSelectedAccountBalance(this.selectedAccount.id)
            });

          }).catch((error) => {
            this.loading = false
            this.fetchingData = false
          })
        }
      }
    },
    groupTransactionsByDate (ungrouped_transactions) {
      // DATE GROUPING
      // this gives an object with dates as keys
      const dateGoups = ungrouped_transactions.reduce((groups, transaction) => {
        const date = transaction.date.split('T')[0]
        if (!groups[date]) groups[date] = []
        groups[date].push(transaction)
        return groups
      }, {})
      const grouped_transactions = Object.keys(dateGoups).map((date) => {
        return { date, transactions: dateGoups[date], _showDetails: true }
      })
      return grouped_transactions
    },
    handleScroll () {
      if (!this.fetchingData && this.nextListUrl) {
        if (this.isInViewport('load_more_button')) {
          this.fetchingData = true
          BankTransactionService.listNextPage(this.nextListUrl).then((response) => {
            // GROUP TRANSACTIONS AND CONCAT
            const grouped_transactions = this.groupTransactionsByDate(response.data.results)
            // check for current last group date, merge transactions if same day
            if(this.transactions[(this.transactions.length - 1)].date === grouped_transactions[0].date) {
              const joined_day_transactions = this.transactions[(this.transactions.length - 1)].transactions.concat(grouped_transactions[0].transactions)
              this.$set(this.transactions[(this.transactions.length - 1)], 'transactions', joined_day_transactions)
              grouped_transactions.shift()
            }
            this.transactions = this.transactions.concat(grouped_transactions)
            this.nextListUrl = response.data.next
            this.fetchingData = false
          })
        }
      }
    },
    isInViewport(element_id) {
      const element = document.getElementById(element_id)
      if (!element) return false
      const rect = element.getBoundingClientRect()
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      )
    },
    updateTransaction (transaction) {
      this.transactions.forEach(group => {
        const index = group.transactions.findIndex(tx => tx.id === transaction.id);
        if (index !== -1) {
          this.$set(group.transactions, index, transaction);
        }
      })
    }
  },
}
</script>

<style lang="scss">
.gk-table {
  &.transactions-dates-table {
    tbody {
      tr {
        border: 0 !important;
        cursor: initial;
        box-shadow: none;
        &.b-table-details {
          > td {
            padding-left: 1rem;
            padding-right: 0;
          }
        }
        &.b-table-has-details {
          .col-dategroup {
            padding: 0;
            padding-top: 1rem;
            font-size: 14px;
            color: $colorTextBase;
            text-transform: uppercase;
          }
        }
      }
    }
  }
  &.transactions-table {
    $colSeparation: 1rem;
    $tdPadding: 0.25rem;
    $rowHeight: 35px;

    $colIconWidth: calc(#{$rowHeight} + #{$colSeparation});
    $colStatusWidth: calc(calc(200px + #{$colSeparation}) - #{$colIconWidth});
    $colDescWidth: calc(100% - calc(#{$colIconWidth} + #{$colStatusWidth}));

    tbody {
      tr {
        cursor: pointer;
        border: 0 !important;
        box-shadow: none;
        &:hover {
          .transaction-td-wrap {
            border-color: $colorBrand !important;
          }
        }
        .col-icon { width: $colIconWidth; }
        .col-transaction-status { width: $colStatusWidth; max-width: $colStatusWidth; }
        .col-descamount { width: $colDescWidth; max-width: 1px; }

        &.transaction-row-in { 
          .transaction-amount { 
            color: #33A04B; 
          }
        }
        &.transaction-row-out { 
          .transaction-amount { 
            color: $colorBrown; 
          }
        }
        
        // Matched 
        &.transaction-row-matched_with_cost,
        &.transaction-row-matched_with_invoice {
          td.col-icon, td.col-transaction-status {
            .transaction-td-wrap {
              background-color: #E6F5E6;
              color: $colorBrand;
              border-color: #E6F5E6;
            }
          }
          td.col-transaction-status {
            .transaction-td-wrap {
              color: $colorTextBase;
            }
          }
        }
        
        // Unassigned
        &.transaction-row-unassigned {
          td.col-icon, td.col-transaction-status {
            .transaction-td-wrap {
              background-color: #FFDB95;
              color: #806E4A;
              border-color: #FFDB95;
            }
          }
          td.col-transaction-status {
            .transaction-td-wrap {
              color: $colorTextBase;
            }
          }
        }
        // Others
        &.transaction-row-private,
        &.transaction-row-taxes,
        &.transaction-row-others,
        &.transaction-row-batch_payments,
        &.transaction-row-loans,
        &.transaction-row-savings {
          td.col-icon, td.col-transaction-status {
            .transaction-td-wrap {
              background-color: #F6F6F6;
              border-color: #F6F6F6;
              color: #A4A4A4;
            }
          }
        }
        td {
          padding: $tdPadding 0;
          height: calc(#{$rowHeight} + calc(2 * #{$tdPadding}));
          .transaction-td-wrap {
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid transparent;
            border-radius: 50em;
            height: $rowHeight;
            transition: border-color 0.1s ease;
          }
          &.col-icon {
            .transaction-td-wrap {
              width: $rowHeight;
            }
          }
          &.col-transaction-status {
            .transaction-td-wrap {
              padding: 0 0.5rem;
              width: calc(#{$colStatusWidth} - #{$colSeparation});
              font-size: 12px;
            }
          }
          &.col-descamount {
            .transaction-td-wrap {
              justify-content: space-between;
              padding: 0 1rem;
              border-color: $colorBorderDash;
              background: #F9F9F9;
            }
            .transaction-desc-wrap {
              display: flex;
              flex: 1;
              overflow: hidden;

              .transaction-description {
                min-width: 50%;
                max-width: 575px; // fix for super long strings with no spaces (was 100%)
                display: -webkit-box;
                -webkit-line-clamp: 1;
                -webkit-box-orient: vertical;
                overflow: hidden;
                font-size: 14px;
                color: $colorTextBase;
                flex: 1;
              }
              .transaction-description {
              }
              .transaction-name {
                text-align: right;
              }
              .transaction-target_account {
              }
              .transaction-name,
              .transaction-target_account {
                flex: 1;
                padding-left: 0.5rem;
                padding-right: 0.5rem;

                text-transform: uppercase;
                font-size: 14px;
                color: $colorUnassigned;
                max-width: 100%;
                display: -webkit-box;
                -webkit-line-clamp: 1;
                -webkit-box-orient: vertical;
                overflow: hidden;
              }

              .transaction-name + .transaction-target_account {
                position: relative;
                padding-left: calc(0.5rem + 3px); 
                &:before {
                  content: '|';
                  position: absolute;
                  left: 0;
                  top: 0;
                  color: $colorUnassigned;
                }
              }
              
            }
            
            .transaction-amount {
              font-family: DINNextLTPro-Medium, sans-serif;
              white-space: nowrap;
              min-width: 100px;
              text-align: right;
              flex-shrink: 0;
            }
          }
        }
      }
      
    }
  }
}
</style>